// ════════════════════════════════════════════════════════════════════════════
// ⚡ ScanBurst — Phase 2.6 Visual confirmation on barcode/auto-add
// ════════════════════════════════════════════════════════════════════════════
// Imperative API:
//   triggerScanBurst({ label: '+1 Coca-Cola', tone: 'success' })
// Renders a large corner overlay that fades in/out (~600ms), never blocks UI.
// ════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { CheckCircle2, ScanLine, AlertTriangle } from 'lucide-react';

type Tone = 'success' | 'warn' | 'error';

interface Burst {
  id: number;
  label: string;
  tone: Tone;
}

let pushRef: ((b: Omit<Burst, 'id'>) => void) | null = null;
let nextId = 0;

export function triggerScanBurst(opts: { label: string; tone?: Tone }) {
  if (pushRef) pushRef({ label: opts.label, tone: opts.tone || 'success' });
}

const toneStyles: Record<Tone, { bg: string; ring: string; text: string; Icon: typeof CheckCircle2 }> = {
  success: {
    bg: 'from-emerald-500 to-cyan-500',
    ring: 'ring-emerald-400/50',
    text: 'text-white',
    Icon: CheckCircle2,
  },
  warn: {
    bg: 'from-amber-500 to-orange-500',
    ring: 'ring-amber-400/50',
    text: 'text-white',
    Icon: ScanLine,
  },
  error: {
    bg: 'from-rose-500 to-red-500',
    ring: 'ring-rose-400/50',
    text: 'text-white',
    Icon: AlertTriangle,
  },
};

export const ScanBurstHost: React.FC = () => {
  const [bursts, setBursts] = useState<Burst[]>([]);
  const timers = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    pushRef = (b) => {
      const id = ++nextId;
      setBursts((prev) => [...prev.slice(-2), { ...b, id }]);
      const t = window.setTimeout(() => {
        setBursts((prev) => prev.filter((x) => x.id !== id));
        timers.current.delete(id);
      }, 900);
      timers.current.set(id, t);
    };
    return () => {
      pushRef = null;
      timers.current.forEach((t) => clearTimeout(t));
      timers.current.clear();
    };
  }, []);

  return createPortal(
    <div className="fixed top-20 right-4 z-[9995] pointer-events-none flex flex-col items-end gap-2">
      <AnimatePresence>
        {bursts.map((b) => {
          const s = toneStyles[b.tone];
          const { Icon } = s;
          return (
            <motion.div
              key={b.id}
              initial={{ opacity: 0, scale: 0.4, x: 40 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.85, x: 20, transition: { duration: 0.2 } }}
              transition={{ type: 'spring', stiffness: 500, damping: 24 }}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-2xl bg-gradient-to-br ${s.bg} ${s.text} shadow-2xl ring-4 ${s.ring} font-bold text-[12px]`}
            >
              <motion.div
                initial={{ rotate: -30, scale: 0.5 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ type: 'spring', stiffness: 600, damping: 18 }}
              >
                <Icon size={15} />
              </motion.div>
              <span className="whitespace-nowrap">{b.label}</span>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>,
    document.body
  );
};
